import React, { useState, useEffect } from 'react';
import axios from 'axios';

interface PerformanceMonitorTabProps {
  onError?: (error: string) => void;
}

const PerformanceMonitorTab: React.FC<PerformanceMonitorTabProps> = ({ onError }) => {
  const [htmlContent, setHtmlContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const fetchPerformanceStatus = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');

        const response = await axios.get('/api/performance/status', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: 'text',
        });

        setHtmlContent(response.data);
        setError('');
      } catch (err: any) {
        const errorMessage =
          err.response?.data?.error?.message || err.message || 'Failed to load performance status';
        setError(errorMessage);
        if (onError) {
          onError(errorMessage);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPerformanceStatus();
  }, [onError]);

  // Attach event listeners for collapsible logs and set up clearLog function after HTML is rendered
  useEffect(() => {
    if (!htmlContent) return;

    // Set up clearLog function in global scope
    (window as any).clearLog = async function () {
      if (
        !confirm(
          'Are you sure you want to clear the performance log? This action cannot be undone.'
        )
      ) {
        return;
      }

      try {
        const token = localStorage.getItem('token') || '';
        if (!token) {
          alert('Error: Authentication token not found. Please refresh the page.');
          return;
        }

        const response = await fetch('/api/performance/clear', {
          method: 'POST',
          headers: {
            Authorization: 'Bearer ' + token,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const result = await response.json();
          alert(result.message || 'Log cleared successfully');
          window.location.reload();
        } else {
          const error = await response.json().catch(() => ({ message: 'Unknown error' }));
          alert('Error clearing log: ' + (error.message || 'Unknown error'));
        }
      } catch (error: any) {
        alert('Error clearing log: ' + (error.message || 'Network error'));
      }
    };

    const toggleLog = (id: string) => {
      const details = document.getElementById(`details-${id}`);
      const toggle = document.getElementById(`toggle-${id}`);

      if (details && toggle) {
        if (details.classList.contains('expanded')) {
          details.classList.remove('expanded');
          toggle.textContent = '▼';
        } else {
          details.classList.add('expanded');
          toggle.textContent = '▲';
        }
      }
    };

    // Attach click handlers to all log summaries
    const logSummaries = document.querySelectorAll('.log-summary[data-log-id]');
    const handlers: Array<{ element: Element; handler: () => void }> = [];

    logSummaries.forEach((summary) => {
      const logId = summary.getAttribute('data-log-id');
      if (logId) {
        const handler = () => toggleLog(logId);
        summary.addEventListener('click', handler);
        handlers.push({ element: summary, handler });
      }
    });

    // Cleanup event listeners
    return () => {
      handlers.forEach(({ element, handler }) => {
        element.removeEventListener('click', handler);
      });
      // Clean up clearLog function
      delete (window as any).clearLog;
    };
  }, [htmlContent]);

  if (loading) {
    return (
      <div className="card">
        <p>Loading performance monitor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <div className="alert alert-error">
          <strong>Error:</strong> {error}
        </div>
        <p style={{ marginTop: '1rem' }}>
          Make sure you're logged in as a Super Admin and that performance monitoring is enabled.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
    </div>
  );
};

export default PerformanceMonitorTab;
